import { isValidEmail } from "../../Shared/Utilities/string";

export const PHONE_E164_WITHOUT_PLUS_PATTERN = /^[1-9]\d{1,14}$/;

// Visitors naturally type separators ("348 876 4760", "348-876-4760"), which PhoneInput.razor
// passes straight through into its bound value. Stripped only for what's validated for/sent to
// OneTrust - the posted Forms value (and so what IHubPostSubmissionActor receives) is untouched.
// Anything else (e.g. a typed "+39"/"0039", which PhoneInput would prefix with the area code
// again) is deliberately left as-is, so it fails the E.164 check rather than being guessed at.
export const normalizeOneTrustPhone = (value: string): string =>
  value.replace(/[\s\-().]/g, "");

export const isValidOneTrustPhone = (value: string): boolean => {
  const normalized = normalizeOneTrustPhone(value);

  return !!normalized && PHONE_E164_WITHOUT_PLUS_PATTERN.test(normalized);
};

// Shared with registerOneTrustConsentValidator (which additionally shows/hides the per-checkbox
// error messages) and with the onBeforeSubmit guard in submitOneTrustConsentCapture, so both
// agree on what "valid" means for the checkboxes actually sent to OneTrust.
export const areRequiredConsentCheckboxesChecked = (marker: HTMLElement): boolean =>
  Array.from(
    marker.querySelectorAll<HTMLInputElement>('input[type="checkbox"][required]')
  ).every(checkbox => checkbox.checked);

// Which contact details a touchpoint's consent must be captured with, as rendered onto its marker
// from OneTrustContactRequirements.FromTouchpoint (see SalesforceConsentElementBlock.cshtml) - e.g.
// marketing-mandatory-email-phone needs both. Anything else gets the SDK's own minimum of one of
// them.
export type ContactRequirement = "EmailAndPhone" | "Email" | "Phone" | "EmailOrPhone";

const CONTACT_REQUIREMENTS: readonly string[] = [
  "EmailAndPhone",
  "Email",
  "Phone",
  "EmailOrPhone"
];

export const getContactRequirement = (marker: HTMLElement | null | undefined) => {
  const requirement = marker?.dataset.ssoccContactRequirement;

  return requirement && CONTACT_REQUIREMENTS.includes(requirement)
    ? (requirement as ContactRequirement)
    : "EmailOrPhone";
};

export type ContactValidity = { isEmailValid: boolean; isPhoneValid: boolean };

export const getContactValidity = (form: HTMLFormElement): ContactValidity => {
  const emailField = form.querySelector<HTMLInputElement>(
    '[data-ssocc-sdk-id="emailId"]'
  );
  const phoneField = form.querySelector<HTMLInputElement>(
    '[data-ssocc-sdk-id="phoneId"]'
  );

  return {
    // Trimmed like field-capture does before sending it.
    isEmailValid: !!emailField?.value.trim() && isValidEmail(emailField.value.trim()),
    isPhoneValid: isValidOneTrustPhone(phoneField?.value ?? "")
  };
};

// The required contact details that aren't present and valid, for telemetry - undefined when the
// requirement is met.
export const getMissingContact = (
  requirement: ContactRequirement,
  { isEmailValid, isPhoneValid }: ContactValidity
): "Email" | "Phone" | "EmailAndPhone" | "EmailOrPhone" | undefined => {
  switch (requirement) {
    case "EmailAndPhone":
      if (!isEmailValid && !isPhoneValid) {
        return "EmailAndPhone";
      }

      if (!isEmailValid) {
        return "Email";
      }

      return isPhoneValid ? undefined : "Phone";
    case "Email":
      return isEmailValid ? undefined : "Email";
    case "Phone":
      return isPhoneValid ? undefined : "Phone";
    default:
      return isEmailValid || isPhoneValid ? undefined : "EmailOrPhone";
  }
};

// Shared with registerOneTrustContactRequiredValidator and the onBeforeSubmit guard in
// submitOneTrustConsentCapture, for the same reason as above. The marker's touchpoint decides
// which contact details are required (see getContactRequirement) - without this, a consent for
// marketing-mandatory-email-phone could reach OneTrust with only one of them, while the CMS
// submission had both.
export const hasValidContactInfo = (
  form: HTMLFormElement,
  marker: HTMLElement
): boolean =>
  getMissingContact(getContactRequirement(marker), getContactValidity(form)) ===
  undefined;
